import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  Copy,
  FileText,
  Palette,
  Layout,
  Settings2,
  GripVertical,
  EyeOff,
  RefreshCw,
  ArrowLeft,
  Upload,
} from 'lucide-react';
// Default values
const DEFAULT_BRANDING = {
  primaryColor: '#24064C',
  secondaryColor: '#FF8154',
  accentColor: '#00FFC5',
  headerGradient: 'linear-gradient(135deg, #24064C 0%, #7C3AED 100%)',
  fontFamily: 'Poppins, sans-serif',
  bodyFontFamily: 'Lato, sans-serif',
};

const DEFAULT_HEADER = {
  showLogo: true,
  logoUrl: '',
  title: 'Assessment Report',
  subtitle: 'The PATH Programme',
  showDate: true,
  showUserName: true,
  backgroundColor: '',
};

const DEFAULT_FOOTER = {
  showLegal: true,
  legalText: 'Secret Sauce is a trading name of Organisation Logic Limited, England & Wales Company Registration Number: 12213127',
  showConfidential: true,
  confidentialText: 'This report is confidential and intended for the recipient only.',
  showTimestamp: true,
};

const DEFAULT_PAGE_SETTINGS = {
  format: 'A4',
  orientation: 'portrait',
  marginTop: '0',
  marginRight: '0',
  marginBottom: '0',
  marginLeft: '0',
};

const SECTION_TYPES = {
  edge: [
    { value: 'primary_result', label: 'Primary Edge Result' },
    { value: 'secondary_result', label: 'Secondary Edge Result' },
    { value: 'analysis', label: 'Deeper Analysis' },
    { value: 'observations', label: 'Key Observations' },
    { value: 'recommendations', label: 'Recommendations' },
    { value: 'custom', label: 'Custom Content' },
  ],
  core: [
    { value: 'primary_result', label: 'Stated Archetype' },
    { value: 'secondary_result', label: 'Core Archetype' },
    { value: 'leadership_style', label: 'Leadership Style' },
    { value: 'strengths', label: 'Key Strengths' },
    { value: 'challenges', label: 'Growth Areas' },
    { value: 'recommendations', label: 'Recommendations' },
    { value: 'custom', label: 'Custom Content' },
  ],
};

export default function AdminPdfTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [saving, setSaving] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [syncingId, setSyncingId] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    templateType: 'edge',
    description: '',
    isActive: true,
    branding: { ...DEFAULT_BRANDING },
    pageSettings: { ...DEFAULT_PAGE_SETTINGS },
    header: { ...DEFAULT_HEADER },
    footer: { ...DEFAULT_FOOTER },
    sections: [],
    customCss: '',
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/pdf-templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      toast.error('Failed to load PDF templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      slug: '',
      templateType: 'edge',
      description: '',
      isActive: true,
      branding: { ...DEFAULT_BRANDING },
      pageSettings: { ...DEFAULT_PAGE_SETTINGS },
      header: { ...DEFAULT_HEADER, title: 'EDGE Diagnostic Report' },
      footer: { ...DEFAULT_FOOTER },
      sections: [],
      customCss: '',
    });
    setShowEditor(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name || '',
      slug: template.slug || '',
      templateType: template.templateType || 'edge',
      description: template.description || '',
      isActive: template.isActive ?? true,
      branding: { ...DEFAULT_BRANDING, ...template.branding },
      pageSettings: { ...DEFAULT_PAGE_SETTINGS, ...template.pageSettings },
      header: { ...DEFAULT_HEADER, ...template.header },
      footer: { ...DEFAULT_FOOTER, ...template.footer },
      sections: template.sections || [],
      customCss: template.customCss || '',
    });
    setShowEditor(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.slug) {
      toast.error('Please fill in template name and slug');
      return;
    }

    try {
      setSaving(true);
      if (editingTemplate) {
        await axios.put(`/api/admin/pdf-templates/${editingTemplate.id}`, {
          name: formData.name,
          description: formData.description,
          isActive: formData.isActive,
          branding: formData.branding,
          pageSettings: formData.pageSettings,
          header: formData.header,
          footer: formData.footer,
          sections: formData.sections,
          customCss: formData.customCss,
        });
        toast.success('Template updated successfully');
      } else {
        await axios.post('/api/admin/pdf-templates', formData);
        toast.success('Template created successfully');
      }
      setShowEditor(false);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Are you sure you want to delete "${template.name}"?`)) return;

    try {
      await axios.delete(`/api/admin/pdf-templates/${template.id}`);
      toast.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const handleDuplicate = async (template) => {
    try {
      await axios.post(`/api/admin/pdf-templates/${template.id}/duplicate`);
      toast.success('Template duplicated successfully');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to duplicate template');
    }
  };

  const handlePreview = useCallback(async () => {
    if (!editingTemplate && !formData.slug) {
      toast.error('Save the template first to preview');
      return;
    }

    try {
      setPreviewLoading(true);
      const templateId = editingTemplate?.id || formData.slug;
      const response = await axios.post(`/api/admin/pdf-templates/${templateId}/preview`);
      setPreviewHtml(response.data.html);
      setShowPreview(true);
    } catch (error) {
      toast.error('Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  }, [editingTemplate, formData.slug]);

  const handlePreviewPdf = useCallback(async () => {
    if (!editingTemplate && !formData.slug) {
      toast.error('Save the template first to preview PDF');
      return;
    }

    try {
      setPreviewLoading(true);
      const templateId = editingTemplate?.id || formData.slug;
      const response = await axios.post(
        `/api/admin/pdf-templates/${templateId}/preview-pdf`,
        {},
        { responseType: 'blob' }
      );
      
      // Create blob URL and open in new tab
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success('PDF preview generated');
    } catch (error) {
      toast.error('Failed to generate PDF preview');
    } finally {
      setPreviewLoading(false);
    }
  }, [editingTemplate, formData.slug]);

  const handleToggleActive = async (template) => {
    try {
      await axios.put(`/api/admin/pdf-templates/${template.id}`, {
        isActive: !template.isActive,
      });
      toast.success(`Template ${template.isActive ? 'deactivated' : 'activated'}`);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to update template status');
    }
  };

  const handleSyncMarkupGo = async (template) => {
    try {
      setSyncingId(template.id);
      const response = await axios.post(`/api/admin/pdf-templates/${template.id}/sync-markupgo`);
      toast.success(response.data.message || 'Template synced to MarkupGo');
      fetchTemplates();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to sync template to MarkupGo';
      toast.error(detail);
    } finally {
      setSyncingId(null);
    }
  };

  const updateBranding = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      branding: { ...prev.branding, [key]: value },
    }));
  };

  const updateHeader = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      header: { ...prev.header, [key]: value },
    }));
  };

  const updateFooter = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      footer: { ...prev.footer, [key]: value },
    }));
  };

  const updateSection = (index, key, value) => {
    setFormData((prev) => {
      const newSections = [...prev.sections];
      newSections[index] = { ...newSections[index], [key]: value };
      return { ...prev, sections: newSections };
    });
  };

  const addSection = () => {
    const newSection = {
      id: `section_${Date.now()}`,
      type: 'custom',
      title: 'New Section',
      visible: true,
      order: formData.sections.length + 1,
      customContent: '<p>Custom content here...</p>',
    };
    setFormData((prev) => ({
      ...prev,
      sections: [...prev.sections, newSection],
    }));
  };

  const removeSection = (index) => {
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index),
    }));
  };

  const moveSection = (index, direction) => {
    const newSections = [...formData.sections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newSections.length) return;
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    // Update order values
    newSections.forEach((s, i) => (s.order = i + 1));
    setFormData((prev) => ({ ...prev, sections: newSections }));
  };

  return (
      <div data-testid="admin-pdf-templates">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold">PDF Report Templates</h1>
            <p className="text-muted-foreground">Customize PDF report design and layout</p>
          </div>
          <Button onClick={handleCreate} data-testid="create-template-btn">
            <Plus className="w-4 h-4 mr-2" /> New Template
          </Button>
        </div>

        {/* Templates List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent" />
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12 bg-card border border-border rounded-[var(--radius)]">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">No PDF templates found</p>
            <Button onClick={handleCreate}>
              <Plus className="w-4 h-4 mr-2" /> Create First Template
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {templates.map((template) => (
              <Card key={template.id} data-testid={`template-${template.slug}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-primary" />
                        {template.name}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                          {template.slug}
                        </code>
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={template.isActive ? 'default' : 'secondary'}>
                        {template.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge variant="outline">
                        {template.templateType === 'edge' ? 'EDGE' : 'CORE'}
                      </Badge>
                      {template.markupgoTemplateId && (
                        <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                          Synced
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {template.description && (
                    <p className="text-sm text-muted-foreground mb-4">{template.description}</p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(template)}
                      data-testid={`edit-${template.slug}`}
                    >
                      <Pencil className="w-4 h-4 mr-1" /> Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          setPreviewLoading(true);
                          const response = await axios.post(
                            `/api/admin/pdf-templates/${template.id}/preview`
                          );
                          setPreviewHtml(response.data.html);
                          setShowPreview(true);
                        } catch (error) {
                          toast.error('Failed to generate preview');
                        } finally {
                          setPreviewLoading(false);
                        }
                      }}
                      data-testid={`preview-${template.slug}`}
                    >
                      <Eye className="w-4 h-4 mr-1" /> Preview
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDuplicate(template)}
                    >
                      <Copy className="w-4 h-4 mr-1" /> Duplicate
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleActive(template)}
                    >
                      {template.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSyncMarkupGo(template)}
                      disabled={syncingId === template.id}
                      data-testid={`sync-${template.slug}`}
                    >
                      {syncingId === template.id ? (
                        <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-1" />
                      )}
                      {template.markupgoTemplateId ? 'Re-sync' : 'Sync'} to MarkupGo
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(template)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`delete-${template.slug}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      {/* Template Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit PDF Template' : 'Create New PDF Template'}
            </DialogTitle>
            <DialogDescription>
              Design your PDF report template with custom branding and sections.
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="basic" className="mt-4">
            <TabsList className="grid grid-cols-5 w-full">
              <TabsTrigger value="basic">
                <Settings2 className="w-4 h-4 mr-1" /> Basic
              </TabsTrigger>
              <TabsTrigger value="branding">
                <Palette className="w-4 h-4 mr-1" /> Branding
              </TabsTrigger>
              <TabsTrigger value="header">
                <Layout className="w-4 h-4 mr-1" /> Header
              </TabsTrigger>
              <TabsTrigger value="sections">
                <FileText className="w-4 h-4 mr-1" /> Sections
              </TabsTrigger>
              <TabsTrigger value="footer">Footer</TabsTrigger>
            </TabsList>

            {/* Basic Settings */}
            <TabsContent value="basic" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Template Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="EDGE Diagnostic Report"
                    data-testid="template-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="slug">Slug *</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        slug: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''),
                      })
                    }
                    placeholder="edge_diagnostic"
                    disabled={!!editingTemplate}
                    data-testid="template-slug-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="templateType">Template Type</Label>
                  <Select
                    value={formData.templateType}
                    onValueChange={(value) => setFormData({ ...formData, templateType: value })}
                    disabled={!!editingTemplate}
                  >
                    <SelectTrigger data-testid="template-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="edge">EDGE Diagnostic</SelectItem>
                      <SelectItem value="core">CORE Diagnostic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    id="isActive"
                    checked={formData.isActive}
                    onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                  />
                  <Label htmlFor="isActive">Active</Label>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this template..."
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor="customCss">Custom CSS (Advanced)</Label>
                <Textarea
                  id="customCss"
                  value={formData.customCss}
                  onChange={(e) => setFormData({ ...formData, customCss: e.target.value })}
                  placeholder="/* Add custom CSS overrides here */"
                  className="font-mono text-sm"
                  rows={4}
                />
              </div>
            </TabsContent>

            {/* Branding Settings */}
            <TabsContent value="branding" className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="primaryColor">Primary Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={formData.branding.primaryColor}
                      onChange={(e) => updateBranding('primaryColor', e.target.value)}
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      value={formData.branding.primaryColor}
                      onChange={(e) => updateBranding('primaryColor', e.target.value)}
                      placeholder="#24064C"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="secondaryColor">Secondary Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={formData.branding.secondaryColor}
                      onChange={(e) => updateBranding('secondaryColor', e.target.value)}
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      value={formData.branding.secondaryColor}
                      onChange={(e) => updateBranding('secondaryColor', e.target.value)}
                      placeholder="#FF8154"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="accentColor">Accent Color</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={formData.branding.accentColor}
                      onChange={(e) => updateBranding('accentColor', e.target.value)}
                      className="w-12 h-10 p-1"
                    />
                    <Input
                      value={formData.branding.accentColor}
                      onChange={(e) => updateBranding('accentColor', e.target.value)}
                      placeholder="#00FFC5"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label htmlFor="headerGradient">Header Gradient</Label>
                <Input
                  value={formData.branding.headerGradient}
                  onChange={(e) => updateBranding('headerGradient', e.target.value)}
                  placeholder="linear-gradient(135deg, #24064C 0%, #7C3AED 100%)"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  CSS gradient for header background
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fontFamily">Heading Font</Label>
                  <Select
                    value={formData.branding.fontFamily}
                    onValueChange={(value) => updateBranding('fontFamily', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Poppins, sans-serif">Poppins</SelectItem>
                      <SelectItem value="Montserrat, sans-serif">Montserrat</SelectItem>
                      <SelectItem value="Playfair Display, serif">Playfair Display</SelectItem>
                      <SelectItem value="Figtree, sans-serif">Figtree</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="bodyFontFamily">Body Font</Label>
                  <Select
                    value={formData.branding.bodyFontFamily}
                    onValueChange={(value) => updateBranding('bodyFontFamily', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Lato, sans-serif">Lato</SelectItem>
                      <SelectItem value="Roboto, sans-serif">Roboto</SelectItem>
                      <SelectItem value="Inter, sans-serif">Inter</SelectItem>
                      <SelectItem value="Source Sans Pro, sans-serif">Source Sans Pro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Preview swatch */}
              <div className="p-4 rounded-lg border border-border">
                <p className="text-sm font-medium mb-2">Color Preview</p>
                <div className="flex gap-2">
                  <div
                    className="w-16 h-16 rounded"
                    style={{ backgroundColor: formData.branding.primaryColor }}
                    title="Primary"
                  />
                  <div
                    className="w-16 h-16 rounded"
                    style={{ backgroundColor: formData.branding.secondaryColor }}
                    title="Secondary"
                  />
                  <div
                    className="w-16 h-16 rounded"
                    style={{ backgroundColor: formData.branding.accentColor }}
                    title="Accent"
                  />
                  <div
                    className="flex-1 h-16 rounded"
                    style={{ background: formData.branding.headerGradient }}
                    title="Header Gradient"
                  />
                </div>
              </div>
            </TabsContent>

            {/* Header Settings */}
            <TabsContent value="header" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="headerTitle">Report Title</Label>
                  <Input
                    value={formData.header.title}
                    onChange={(e) => updateHeader('title', e.target.value)}
                    placeholder="EDGE Diagnostic Report"
                  />
                </div>
                <div>
                  <Label htmlFor="headerSubtitle">Subtitle</Label>
                  <Input
                    value={formData.header.subtitle}
                    onChange={(e) => updateHeader('subtitle', e.target.value)}
                    placeholder="The PATH Programme"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="logoUrl">Logo URL</Label>
                <Input
                  value={formData.header.logoUrl}
                  onChange={(e) => updateHeader('logoUrl', e.target.value)}
                  placeholder="https://example.com/logo.png"
                />
              </div>

              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    id="showLogo"
                    checked={formData.header.showLogo}
                    onCheckedChange={(checked) => updateHeader('showLogo', checked)}
                  />
                  <Label htmlFor="showLogo">Show Logo</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="showUserName"
                    checked={formData.header.showUserName}
                    onCheckedChange={(checked) => updateHeader('showUserName', checked)}
                  />
                  <Label htmlFor="showUserName">Show User Name</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="showDate"
                    checked={formData.header.showDate}
                    onCheckedChange={(checked) => updateHeader('showDate', checked)}
                  />
                  <Label htmlFor="showDate">Show Date</Label>
                </div>
              </div>
            </TabsContent>

            {/* Sections Settings */}
            <TabsContent value="sections" className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  Configure which sections appear in the report and their order.
                </p>
                <Button variant="outline" size="sm" onClick={addSection}>
                  <Plus className="w-4 h-4 mr-1" /> Add Section
                </Button>
              </div>

              {formData.sections.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-border rounded-lg">
                  <p className="text-muted-foreground">No sections configured.</p>
                  <p className="text-sm text-muted-foreground">
                    Default sections will be used based on template type.
                  </p>
                </div>
              ) : (
                <Accordion type="multiple" className="space-y-2">
                  {formData.sections
                    .sort((a, b) => a.order - b.order)
                    .map((section, index) => (
                      <AccordionItem
                        key={section.id}
                        value={section.id}
                        className="border border-border rounded-lg px-4"
                      >
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-3">
                            <GripVertical className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium">{section.title}</span>
                            {!section.visible && (
                              <EyeOff className="w-4 h-4 text-muted-foreground" />
                            )}
                            <Badge variant="outline" className="text-xs">
                              {section.type}
                            </Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-4 space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Section Title</Label>
                              <Input
                                value={section.title}
                                onChange={(e) => updateSection(index, 'title', e.target.value)}
                              />
                            </div>
                            <div>
                              <Label>Section Type</Label>
                              <Select
                                value={section.type}
                                onValueChange={(value) => updateSection(index, 'type', value)}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {SECTION_TYPES[formData.templateType]?.map((type) => (
                                    <SelectItem key={type.value} value={type.value}>
                                      {type.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          {section.type === 'custom' && (
                            <div>
                              <Label>Custom HTML Content</Label>
                              <Textarea
                                value={section.customContent || ''}
                                onChange={(e) =>
                                  updateSection(index, 'customContent', e.target.value)
                                }
                                placeholder="<p>Your custom content here...</p>"
                                className="font-mono text-sm"
                                rows={4}
                              />
                            </div>
                          )}

                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={section.visible}
                                onCheckedChange={(checked) =>
                                  updateSection(index, 'visible', checked)
                                }
                              />
                              <Label>Visible</Label>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => moveSection(index, 'up')}
                                disabled={index === 0}
                              >
                                ↑ Up
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => moveSection(index, 'down')}
                                disabled={index === formData.sections.length - 1}
                              >
                                ↓ Down
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeSection(index)}
                                className="text-destructive"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                </Accordion>
              )}
            </TabsContent>

            {/* Footer Settings */}
            <TabsContent value="footer" className="space-y-4">
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    id="showLegal"
                    checked={formData.footer.showLegal}
                    onCheckedChange={(checked) => updateFooter('showLegal', checked)}
                  />
                  <Label htmlFor="showLegal">Show Legal Text</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="showConfidential"
                    checked={formData.footer.showConfidential}
                    onCheckedChange={(checked) => updateFooter('showConfidential', checked)}
                  />
                  <Label htmlFor="showConfidential">Show Confidential Notice</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="showTimestamp"
                    checked={formData.footer.showTimestamp}
                    onCheckedChange={(checked) => updateFooter('showTimestamp', checked)}
                  />
                  <Label htmlFor="showTimestamp">Show Timestamp</Label>
                </div>
              </div>

              {formData.footer.showLegal && (
                <div>
                  <Label htmlFor="legalText">Legal Text</Label>
                  <Textarea
                    value={formData.footer.legalText}
                    onChange={(e) => updateFooter('legalText', e.target.value)}
                    rows={2}
                  />
                </div>
              )}

              {formData.footer.showConfidential && (
                <div>
                  <Label htmlFor="confidentialText">Confidential Notice</Label>
                  <Input
                    value={formData.footer.confidentialText}
                    onChange={(e) => updateFooter('confidentialText', e.target.value)}
                  />
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="gap-2 mt-6">
            {editingTemplate && (
              <>
                <Button variant="outline" onClick={handlePreview} disabled={previewLoading}>
                  <Eye className="w-4 h-4 mr-2" />
                  {previewLoading ? 'Loading...' : 'Preview HTML'}
                </Button>
                <Button variant="outline" onClick={handlePreviewPdf} disabled={previewLoading}>
                  <FileText className="w-4 h-4 mr-2" />
                  {previewLoading ? 'Generating...' : 'Preview PDF'}
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setShowEditor(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
              {saving ? 'Saving...' : editingTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" /> PDF Template Preview
            </DialogTitle>
            <DialogDescription>
              Preview of how the PDF report will look with sample data.
            </DialogDescription>
          </DialogHeader>
          <div className="border border-border rounded-lg overflow-hidden mt-4 bg-white">
            <iframe
              srcDoc={previewHtml}
              title="PDF Preview"
              className="w-full h-[600px]"
              style={{ border: 'none' }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
  );
}
